import type { Patient } from 'fhir/r4';
import { useEffect, useMemo } from 'react';
import { Loader, Message, Card } from 'semantic-ui-react';
import useSWR from 'swr';

import { useAuthContext } from '../../../../contexts/AuthContext';
import FHIRFetcher from '../../../../data/FHIRFetcher';
import type { ApiFetchError } from '../../../../services/api-client';
import MemberCard from '../card';

import { normalizeSearchString, buildSearchParam, buildEligibilityParam, commonSWROptions } from './members-list-utils';

interface MembersListProps {
  payer: string;
  searchString: string;
  selectedEligibilityGroup?: string | null;
  refresh: boolean;
  setRefresh: (value: boolean) => void;
  onPatientSelect: (patient: Patient) => void;
}

function MembersList(props: MembersListProps) {
  const authInfo = useAuthContext();

  const handlePatientSelect = (selectedPatient: Patient) => {
    props.onPatientSelect(selectedPatient);
  };

  const normalizedSearchString = useMemo(() => normalizeSearchString(props.searchString), [props.searchString]);

  const options = useMemo(
    () => ({
      ...commonSWROptions,
    }),
    []
  );

  const key = useMemo(() => {
    if (props.payer === 'ALL') {
      return normalizedSearchString
        ? `/Patient/?identifier:not=INACTIVE${buildSearchParam(normalizedSearchString)}`
        : null;
    } else {
      const baseQuery = `/Patient/?identifier:not=INACTIVE&organization=${props.payer}`;
      const searchParam = buildSearchParam(normalizedSearchString);
      const eligibilityParam = buildEligibilityParam(props.selectedEligibilityGroup);

      return `${baseQuery}${searchParam}${eligibilityParam}`;
    }
  }, [props.payer, normalizedSearchString, props.selectedEligibilityGroup]);

  const {
    data: patientData,
    error: patientError,
    isValidating: patientsDataLoading,
    mutate: patientsDataMutate,
  } = useSWR<fhir4.Bundle | undefined, ApiFetchError>(
    key ? [key, authInfo.token] : null,
    FHIRFetcher<fhir4.Bundle>,
    options,
  );

  useEffect(() => {
    if (props.refresh === true) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget SWR revalidation; a rejection only leaves the view stale
      patientsDataMutate();
      props.setRefresh(false);
    }
  }, [props.refresh, patientsDataMutate, props.setRefresh]);

  const hasResults = patientData?.entry && patientData.entry.length > 0;
  const showNoResults = !patientsDataLoading && patientData && !hasResults;
  const isInitialState = !key && !patientsDataLoading && !patientData;

  return (
    <div>
      <p />

      {patientsDataLoading && <Loader active inline="centered" content="Loading members..." />}

      {isInitialState && (
        <Message info>
          <Message.Header>Search for Members</Message.Header>
          <p>
            {props.payer === 'ALL'
              ? 'Enter a search term to find members across all organizations.'
              : 'Use the search bar to find specific members, or view all members in the selected organization.'}
          </p>
        </Message>
      )}

      {showNoResults && <Message warning header="No members found!" content="Please refine your search." />}

      {hasResults && (
        <Card.Group>
          {patientData.entry!.map((patientEntry) => {
            return (
              <MemberCard
                key={patientEntry.resource!.id}
                // This is a `/Patient/?...` search, so every entry carries a
                // `Patient`; `BundleEntry.resource` is `0..1` and the whole
                // `FhirResource` union, which the existing `!` above already assumes.
                patient={patientEntry.resource as Patient}
                onPatientSelect={handlePatientSelect}
              />
            );
          })}
        </Card.Group>
      )}

      {patientError && (
        <Message negative>
          <Message.Header>Oops!. Something went wrong.. </Message.Header>
          {/* SUSPECTED DEFECT: renders empty on the string-rejection branch. See `ApiFetchError`. */}
          <p>{(patientError as { message?: string }).message}</p>
        </Message>
      )}
    </div>
  );
}

export default MembersList;
